/**
 * Rewrites the first .ai-section of LLM module-summary HTML into a
 * title-only overview. Progress meta (Total Time, Video, Quiz, …) is
 * dropped — that data already lives elsewhere on the module page.
 * Later sections (Insights, Study Advice) are left alone.
 *
 * Safe to call repeatedly — already-enhanced markup is normalized again
 * so stale metric cards from cached HTML are stripped.
 */
export function enhanceAiSummaryHtml(html: string | null | undefined): string | null {
  if (!html) {
    return null;
  }

  const trimmed = html.trim();
  if (!trimmed) {
    return null;
  }

  if (typeof DOMParser === 'undefined') {
    return trimmed;
  }

  const doc = new DOMParser().parseFromString(wrapIfNeeded(trimmed), 'text/html');
  const container = doc.querySelector('.ai-summary-container') || doc.body;
  const firstSection = container.querySelector('.ai-section');

  if (!firstSection) {
    return trimmed;
  }

  // Strip metric cards from previously enhanced markup, then continue so
  // narrative tidy still runs.
  const existingOverview = firstSection.querySelector('.ai-overview');
  if (existingOverview) {
    existingOverview.querySelectorAll('.ai-metrics').forEach(el => el.remove());
    tidyNarrativeSections(container);
    return serializeContainer(container, doc, trimmed);
  }

  const titleEl = firstSection.querySelector('.ai-section-title');
  const bodyEl = firstSection.querySelector('.ai-section-body');
  const listItems = bodyEl
    ? Array.from(bodyEl.querySelectorAll(':scope > ul > li'))
    : [];

  // Only rewrite when the first section looks like the progress meta list
  // (Label: Value items). Anything else stays as authored.
  const looksLikeProgressMeta = listItems.length > 0
    && listItems.every(li => /^[^:]+:\s*.+/.test((li.textContent || '').trim()));

  if (!titleEl || !looksLikeProgressMeta) {
    tidyNarrativeSections(container);
    return serializeContainer(container, doc, trimmed);
  }

  firstSection.classList.add('ai-section--overview');

  const overview = doc.createElement('div');
  overview.className = 'ai-overview';

  const title = doc.createElement('h2');
  title.className = 'ai-overview-title';
  title.textContent = cleanOverviewTitle(titleEl.textContent || '');
  overview.appendChild(title);

  firstSection.innerHTML = '';
  firstSection.appendChild(overview);

  // The narrative HTML from the model is frequently malformed — a
  // .ai-section-body emitted as a *sibling* of its title-only section, or
  // lists nested inside <p> (which the parser splits, leaving empty <p>
  // shells behind). Tidy it so the typography rules downstream have a
  // predictable tree to style.
  tidyNarrativeSections(container);

  return serializeContainer(container, doc, trimmed);
}

function serializeContainer(container: Element, doc: Document, fallback: string): string {
  const root = container.classList?.contains('ai-summary-container')
    ? container
    : doc.body.querySelector('.ai-summary-container');

  return root ? root.outerHTML : fallback;
}

/**
 * Reunites orphaned .ai-section-body nodes with their section and removes
 * empty <p> shells left behind when the parser splits a list out of a
 * paragraph. Leaves already-well-formed sections untouched.
 */
function tidyNarrativeSections(container: Element): void {
  const sections = Array.from(container.querySelectorAll('.ai-section'));
  for (const section of sections) {
    if (section.classList.contains('ai-section--overview')) {
      continue;
    }
    if (section.querySelector('.ai-section-body')) {
      continue;
    }

    // Adopt any immediately-following sibling bodies the model detached.
    let sibling = section.nextElementSibling;
    while (sibling && sibling.classList.contains('ai-section-body')) {
      const next = sibling.nextElementSibling;
      section.appendChild(sibling);
      sibling = next;
    }
  }

  container.querySelectorAll('p').forEach(p => {
    if (!p.textContent?.trim() && p.children.length === 0) {
      p.remove();
    }
  });
}

function wrapIfNeeded(html: string): string {
  if (/ai-summary-container/i.test(html)) {
    return html;
  }
  return `<div class="ai-summary-container">${html}</div>`;
}

function cleanOverviewTitle(raw: string): string {
  return raw.replace(/\s+Summary\s*$/i, '').trim() || raw.trim();
}
